import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TermsAcceptanceLabel } from '@/lib/terms-acceptance';
import { z } from 'zod';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

const signupSchema = z
  .object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

const forgotSchema = z.object({
  email: z.string().email('Invalid email address'),
});

type TabValue = 'login' | 'signup' | 'forgot';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<TabValue>('login');
  const [forgotSent, setForgotSent] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const { signIn, signUp, resetPasswordForEmail } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/app';

  const resetForm = () => {
    setError(null);
    setForgotSent(false);
    setSignupSuccess(false);
    setAgreeToTerms(false);
  };

  const handleTabChange = (v: string) => {
    setTab(v as TabValue);
    resetForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (tab === 'login') {
        const validation = loginSchema.safeParse({ email, password });
        if (!validation.success) {
          setError(validation.error.errors[0].message);
          setLoading(false);
          return;
        }
        const { error } = await signIn(email, password);
        if (error) {
          if (error.message.includes('Invalid login credentials')) {
            setError('Invalid email or password');
          } else if (error.message.toLowerCase().includes('pending approval')) {
            setError('Your account is pending approval. An administrator will approve your access soon.');
          } else {
            setError(error.message);
          }
        } else {
          navigate(from, { replace: true });
        }
      } else if (tab === 'signup') {
        if (!agreeToTerms) {
          setError('You must agree to the Terms of Service and Privacy Policy to create an account.');
          setLoading(false);
          return;
        }
        const validation = signupSchema.safeParse({
          email,
          password,
          confirmPassword,
        });
        if (!validation.success) {
          setError(validation.error.errors[0].message);
          setLoading(false);
          return;
        }
        const { error, session } = await signUp(email, password, {
          terms_accepted: true,
          terms_accepted_at: new Date().toISOString(),
        });
        if (error) {
          if (error.message.includes('already registered')) {
            setError('An account with this email already exists');
          } else if (error.message.toLowerCase().includes('pending approval')) {
            setError('Your account has been created and is pending approval. An administrator will approve your access soon.');
          } else {
            setError(error.message);
          }
        } else if (session) {
          navigate(from, { replace: true });
        } else {
          setSignupSuccess(true);
          // Email confirmation required; don't navigate
        }
      } else {
        // forgot
        const validation = forgotSchema.safeParse({ email });
        if (!validation.success) {
          setError(validation.error.errors[0].message);
          setLoading(false);
          return;
        }
        const { error } = await resetPasswordForEmail(email);
        if (error) {
          setError(error.message);
        } else {
          setForgotSent(true);
        }
      }
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-wt-bg-primary text-wt-text-primary p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center mb-4">
            <Logo variant="fullTransparent" size="h-16" />
          </Link>
          <h1 className="font-sans text-2xl font-bold text-wt-text-primary">Watt Tower</h1>
          <p className="font-sans text-wt-text-secondary mt-1 text-sm">
            Energy intelligence for off-grid systems
          </p>
        </div>

        <div className="rounded-lg border border-wt-border-default bg-wt-bg-secondary p-6">
          {tab !== 'forgot' && (
            <Tabs value={tab} onValueChange={handleTabChange} className="mb-6">
              <TabsList className="grid w-full grid-cols-2 bg-wt-bg-tertiary border border-wt-border-default">
                <TabsTrigger
                  value="login"
                  className="font-sans text-wt-text-secondary data-[state=active]:bg-wt-border-default data-[state=active]:text-wt-text-primary"
                >
                  Sign in
                </TabsTrigger>
                <TabsTrigger
                  value="signup"
                  className="font-sans text-wt-text-secondary data-[state=active]:bg-wt-border-default data-[state=active]:text-wt-text-primary"
                >
                  Sign up
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          {(forgotSent || (tab === 'signup' && signupSuccess)) ? (
            <div className="space-y-4">
              {forgotSent ? (
                <>
                  <p className="font-sans text-wt-text-primary">
                    Check your email for a link to reset your password. The link will expire in an hour.
                  </p>
                  <p className="font-sans text-sm text-wt-text-muted">
                    Didn’t receive it? Check spam or{' '}
                    <button
                      type="button"
                      onClick={() => { setForgotSent(false); setError(null); }}
                      className="text-primary hover:underline"
                    >
                      try again
                    </button>
                  </p>
                </>
              ) : (
                <p className="font-sans text-wt-text-primary">
                  Check your email to confirm your account. Click the link we sent you, then sign in.
                </p>
              )}
              <Button
                type="button"
                variant="outline"
                className="w-full border-wt-border-default text-wt-text-secondary hover:bg-wt-bg-tertiary"
                onClick={() => { setTab('login'); setForgotSent(false); setSignupSuccess(false); }}
              >
                Back to sign in
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="font-sans text-wt-text-secondary">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="bg-wt-bg-tertiary border-wt-border-default text-wt-text-primary placeholder:text-wt-text-muted focus:border-wt-slate-blue"
                />
              </div>

              {tab !== 'forgot' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="password" className="font-sans text-wt-text-secondary">
                      Password
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required={tab === 'login'}
                      minLength={tab === 'signup' ? 8 : undefined}
                      autoComplete={tab === 'login' ? 'current-password' : 'new-password'}
                      className="bg-wt-bg-tertiary border-wt-border-default text-wt-text-primary placeholder:text-wt-text-muted focus:border-wt-slate-blue"
                    />
                    {tab === 'signup' && (
                      <p className="font-sans text-xs text-wt-text-muted">
                        At least 8 characters
                      </p>
                    )}
                  </div>
                  {tab === 'signup' && (
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword" className="font-sans text-wt-text-secondary">
                        Confirm password
                      </Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        autoComplete="new-password"
                        className="bg-wt-bg-tertiary border-wt-border-default text-wt-text-primary placeholder:text-wt-text-muted focus:border-wt-slate-blue"
                      />
                    </div>
                  )}
                </>
              )}

              {tab === 'login' && (
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => { setTab('forgot'); resetForm(); }}
                    className="font-sans text-sm text-wt-text-muted hover:text-primary"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              {error && (
                <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm font-sans">
                  {error}
                </div>
              )}

              {tab === 'signup' && (
                <div className="flex items-start gap-3 rounded-md border border-wt-border-default bg-wt-bg-tertiary p-4">
                  <Checkbox
                    id="agreeToTerms"
                    checked={agreeToTerms}
                    onCheckedChange={(v) => setAgreeToTerms(v === true)}
                    className="mt-0.5 border-wt-slate-blue data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                  />
                  <Label
                    htmlFor="agreeToTerms"
                    className="font-sans text-sm font-normal leading-relaxed text-wt-text-secondary cursor-pointer"
                  >
                    <TermsAcceptanceLabel linkClassName="text-primary hover:underline" />
                  </Label>
                </div>
              )}

              <Button
                type="submit"
                className="w-full h-11 bg-primary font-sans font-semibold text-wt-bg-primary hover:bg-wt-green-muted"
                disabled={loading || (tab === 'signup' && !agreeToTerms)}
              >
                {loading
                  ? 'Please wait…'
                  : tab === 'login'
                    ? 'Sign in'
                    : tab === 'signup'
                      ? 'Create account'
                      : 'Send reset link'}
              </Button>
            </form>
          )}

          {tab !== 'forgot' && !forgotSent && (
            <p className="font-sans text-xs text-wt-text-muted text-center mt-6">
              {tab === 'login' ? (
                <>Don’t have an account? Switch to <strong className="text-wt-text-secondary">Sign up</strong> above.</>
              ) : (
                <>Already have an account? Switch to <strong className="text-wt-text-secondary">Sign in</strong> above.</>
              )}
            </p>
          )}

          {tab === 'forgot' && !forgotSent && (
            <p className="font-sans text-xs text-wt-text-muted text-center mt-4">
              <button
                type="button"
                onClick={() => setTab('login')}
                className="text-wt-text-secondary hover:text-wt-text-primary"
              >
                ← Back to sign in
              </button>
            </p>
          )}
        </div>

        <p className="font-sans text-xs text-wt-text-muted text-center mt-6">
          <Link to="/" className="text-wt-text-secondary hover:text-wt-text-primary">
            ← Back to home
          </Link>
        </p>
      </div>
    </div>
  );
}
